import { useQueryClient } from '@tanstack/react-query';
import { type ImportSourceRequest, LOCALES, type Locale, importSourceSchema } from '@shop/types';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input, Select, Textarea } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import type { ImportSource } from '../../lib/types';

/** Пример маппинга CSV — отправная точка для нового источника. */
export const MAPPING_TEMPLATE = {
  format: 'csv',
  encoding: 'utf-8',
  csv: { delimiter: ';', quote: '"', skipLines: 0 },
  fields: {
    supplierSku: { source: 'code', transforms: [{ op: 'trim' }] },
    cost: { source: 'price', transforms: [{ op: 'number' }] },
    stockQty: { source: 'qty', transforms: [{ op: 'number' }] },
    gtin: { source: 'ean', transforms: [{ op: 'digits' }] },
    brand: { source: 'brand', transforms: [] },
    title: { source: 'name', transforms: [] },
  },
  defaults: { currency: 'EUR' },
};

export function SourceDialog({
  supplierId,
  existing,
  onClose,
}: {
  supplierId: string;
  existing?: ImportSource;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const transport = existing?.config.transport;
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    feedMode: existing?.config.feedMode ?? 'full',
    contentLocale: existing?.config.contentLocale ?? 'en',
    transportType: transport?.type ?? 'upload',
    url: transport?.type === 'url' ? transport.url : '',
    intervalMinutes: transport?.type === 'url' ? String(transport.intervalMinutes) : '60',
    maxZeroedStockPct: '30',
    maxPriceChangedPct: '30',
    priceChangeThresholdPct: '20',
    mapping: JSON.stringify(existing?.mapping ?? MAPPING_TEMPLATE, null, 2),
  });
  const [error, setError] = useState<string>();
  const set = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
  };

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setError(undefined);
    let mapping: unknown;
    try {
      mapping = JSON.parse(form.mapping);
    } catch {
      setError(t('suppliers.invalidJson'));
      return;
    }
    const candidate = {
      name: form.name,
      feedMode: form.feedMode,
      contentLocale: form.contentLocale,
      transport:
        form.transportType === 'url'
          ? { type: 'url', url: form.url, intervalMinutes: Number(form.intervalMinutes) }
          : { type: 'upload' },
      mapping,
      guards: {
        maxZeroedStockPct: Number(form.maxZeroedStockPct),
        maxPriceChangedPct: Number(form.maxPriceChangedPct),
        priceChangeThresholdPct: Number(form.priceChangeThresholdPct),
      },
    };
    // Та же схема, что на сервере: ошибки видны до отправки.
    const parsed = importSourceSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n'));
      return;
    }
    const body: ImportSourceRequest = parsed.data;
    try {
      if (existing) {
        await api(`/suppliers/sources/${existing.id}`, { method: 'PUT', body });
      } else {
        await api(`/suppliers/${supplierId}/sources`, { method: 'POST', body });
      }
      await queryClient.invalidateQueries({ queryKey: ['suppliers', supplierId, 'sources'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={existing ? existing.name : t('suppliers.newSource')}
      className="max-w-3xl"
    >
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('suppliers.sourceName')}>
            {(id) => (
              <Input
                id={id}
                required
                value={form.name}
                onChange={(e) => {
                  set({ name: e.target.value });
                }}
              />
            )}
          </Field>
          <Field label={t('suppliers.feedMode')}>
            {(id) => (
              <Select
                id={id}
                value={form.feedMode}
                onChange={(e) => {
                  set({ feedMode: e.target.value as 'full' | 'delta' });
                }}
              >
                <option value="full">{t('suppliers.feed.full')}</option>
                <option value="delta">{t('suppliers.feed.delta')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('suppliers.contentLocale')}>
            {(id) => (
              <Select
                id={id}
                value={form.contentLocale}
                onChange={(e) => {
                  set({ contentLocale: e.target.value as Locale });
                }}
              >
                {LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {l.toUpperCase()}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('suppliers.transport')}>
            {(id) => (
              <Select
                id={id}
                value={form.transportType}
                onChange={(e) => {
                  set({ transportType: e.target.value as 'upload' | 'url' });
                }}
              >
                <option value="upload">{t('suppliers.transportType.upload')}</option>
                <option value="url">{t('suppliers.transportType.url')}</option>
              </Select>
            )}
          </Field>
          {form.transportType === 'url' ? (
            <>
              <Field label={t('suppliers.url')}>
                {(id) => (
                  <Input
                    id={id}
                    type="url"
                    required
                    value={form.url}
                    onChange={(e) => {
                      set({ url: e.target.value });
                    }}
                  />
                )}
              </Field>
              <Field label={t('suppliers.interval')}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={5}
                    value={form.intervalMinutes}
                    onChange={(e) => {
                      set({ intervalMinutes: e.target.value });
                    }}
                  />
                )}
              </Field>
            </>
          ) : null}
        </div>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">{t('suppliers.guards')}</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            {(
              [
                ['maxZeroedStockPct', t('suppliers.maxZeroed')],
                ['maxPriceChangedPct', t('suppliers.maxPriceChanged')],
                ['priceChangeThresholdPct', t('suppliers.priceThreshold')],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={label}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={0}
                    value={form[key]}
                    onChange={(e) => {
                      set({ [key]: e.target.value });
                    }}
                  />
                )}
              </Field>
            ))}
          </div>
        </fieldset>
        <Field label={t('suppliers.mapping')} hint={t('suppliers.mappingHint')}>
          {(id) => (
            <Textarea
              id={id}
              rows={14}
              spellCheck={false}
              className="font-mono text-xs"
              value={form.mapping}
              onChange={(e) => {
                set({ mapping: e.target.value });
              }}
            />
          )}
        </Field>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('common.save')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
